import { Input, InputAdornment, Checkbox } from '@material-ui/core';
import React, {
  createRef,
  useCallback, useEffect, useState,
} from 'react';
import DrawerMenu from 'react-modern-drawer';
import clsx from 'clsx';
import AddIcon from '@material-ui/icons/Add';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Spinner from '../../DesignSystem/spinner';
import CloseBtn from '../../../assets/images/close-drawer.svg';
import SearchIconBtn from '../../../assets/images/search.svg';
import CheckboxIcon from '../../../assets/images/checkbox-unchecked.svg';
import CheckboxCheckedIcon from '../../../assets/images/checkbox-checked.svg';
import WaveXchangeIcon from '../../../assets/images/wavexchange-small-icon.svg';
import Button from '../../DesignSystem/button';
import { useSet, useNetworkElement } from '../../../utils/customHooks';
import Dropdown from '../../DesignSystem/dropdown_checkbox';
import PortGreen from '../../../assets/images/port-green.svg';
import PortGrey from '../../../assets/images/port-grey.svg';
import { hasResources, hasVerified } from '../../../utils/methods';
import { addNEType as submitNEType, editNEPayload } from '../../../utils/appConstants';
import NodeForm from './NodeForm';
import EditIcon from '../../../assets/images/edit-icon.svg';

interface SelectNEDrawerProps {
  selectedNes: any[];
  loading: boolean;
  open: boolean;
  onNeOpen: () => void;
  onNeClose: (_?:any) => void;
  networkElements: any[];
  addNetworkElement: (data: submitNEType) => void;
  editNetworkElement: (payload: editNEPayload) => void;
}

const SelectNEDrawer: React.FC<SelectNEDrawerProps> = ({
  selectedNes: startingSelectedNes, loading, open, onNeClose, onNeOpen, networkElements: startingNetworkElements, addNetworkElement, editNetworkElement,
}) => {
  // this holds the currently displayed network elements
  const {
    networkElements, NEArray, filterNetworkElements,
  } = useNetworkElement(startingNetworkElements, open);

  // holds logic for selected NEs
  const {
    array: selectedNEs, toggle: toggleNE, union, difference, isSubset: allChecked, areEqual,
  } = useSet(startingSelectedNes, open);

  /* The above logic is as follows:
  The Hook networkElements has all the logic for the elements displayed in the drawer.
  NEArray is an array of only the NE Ids, and networkELements has the whole NE object. filterNetworkElements changes the NEs to display

  The Hook useSet has all the logic for the selected NEs, displayed or not display it doesn't matter.
  The set only exists in the useSet hook, the 'selectedNEs' variable will be in array form.
  ToggleNE switches if its selected or not. Union and difference are used for select all/unselect all.
  allChecked is a memoized variable to return if all the NEs are selected
  */

  const [filters, setFilters] = useState<dropdownFilter>({
    filterByBD: false, filterByNR: false, filterByOnNet: false, filterByPrivate: false,
  });
  const [dropdownValue, setDropdownValue] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [nodePage, setNodePage] = useState<'add' | 'edit' | 'closed'>('closed');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [longitude, setLongitude] = useState('');
  const [latitude, setLatitude] = useState('');
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [street, setStreet] = useState('');
  const [identifiers, setIdentifiers] = useState<{neId: number, nodeId: number} | null>();
  const [startingNode, setStartingNode] = useState<any>();
  const [showOnMap, setShowOnMap] = useState<boolean>(false);

  useEffect(() => {
    setFilters({
      filterByBD: false, filterByNR: false, filterByOnNet: false, filterByPrivate: false,
    });
    setDropdownValue([]);
    setSearch('');
  }, [open]);

  type dropdownFilter = { filterByNR: boolean, filterByBD: boolean, filterByOnNet: boolean, filterByPrivate: boolean };
  const dropdownList = ['Has Ports', 'Has Links', 'On WaveXchange', 'Private'];
  const handleDropdownChange = (e: any) => {
    setDropdownValue(e.target.value);
    const tempFilter: dropdownFilter = {
      filterByBD: false, filterByNR: false, filterByOnNet: false, filterByPrivate: false,
    };
    if (e.target.value.includes(dropdownList[0])) { tempFilter.filterByNR = true; }
    if (e.target.value.includes(dropdownList[1])) { tempFilter.filterByBD = true; }
    if (e.target.value.includes(dropdownList[2])) { tempFilter.filterByOnNet = true; }
    if (e.target.value.includes(dropdownList[3])) { tempFilter.filterByPrivate = true; }
    setFilters(tempFilter);
    filterNetworkElements(tempFilter, search);
  };

  const customDisplay = (): string => {
    const displayArray: string[] = [];
    if (filters.filterByOnNet) displayArray.push('On WV');
    if (filters.filterByNR) displayArray.push('Has ports');
    if (filters.filterByBD) displayArray.push('Has links');
    if (filters.filterByPrivate) displayArray.push('Is private');
    return displayArray.join(', ');
  };

  const handleSearchChange = (e: any) => {
    setSearch(e.target.value);
    filterNetworkElements(filters, e.target.value);
  };

  const handleBack = useCallback(() => {
    setNodePage('closed');
    setName('');
    setCity('');
    setState('');
    setZip('');
    setStreet('');
    setCountry('');
    setLongitude('');
    setLatitude('');
    setShowOnMap(false);
    setIdentifiers(null);
    setStartingNode(null);
  }, []);

  const handleEditNEOpen = (e: any, ne: any) => {
    e.preventDefault();
    setNodePage('edit');
    setShowOnMap(ne.longitude && ne.latitude);
    setName(ne.ne_name || '');
    setCity(ne.city || '');
    setState(ne.state || '');
    setZip(ne.zip || '');
    setStreet(ne.street || '');
    setCountry(ne.country || '');
    setLongitude(ne.longitude || '');
    setLatitude(ne.latitude || '');
    setStartingNode(ne);
    setIdentifiers({ neId: ne.id, nodeId: ne.node_id });
  };

  let neRows;
  if (loading) {
    neRows = (
      <div className="row loading">
        <Spinner size={30} />
      </div>
    );
  } else if (networkElements.length <= 0) {
    neRows = (
      <div className="row empty">
        <label>No nodes found for given text.</label>
      </div>
    );
  } else {
    const addNodeButton = (
      <div className="row add" key={-1}>
        <FormControlLabel
          control={(
            <Checkbox
              onClick={() => setNodePage('add')}
              icon={<AddIcon color="inherit" />}
            />
          )}
          label="Add Private Node"
        />
      </div>
    );
    const allSelected = allChecked(NEArray);
    const selectAllButton = (
      <div className="row" key={0}>
        <FormControlLabel
          control={(
            <Checkbox
              classes={{
                root: 'selection-checkbox',
              }}
              color="default"
              checked={allSelected}
              onChange={allSelected ? () => difference(NEArray) : () => union(NEArray)}
              icon={<img src={CheckboxIcon} alt="Checkbox" className="checkbox-icon" />}
              checkedIcon={<img src={CheckboxCheckedIcon} alt="Checkbox Checked" className="checkbox-checked-icon" />}
            />
          )}
          label="Select all"
        />
      </div>
    );
    neRows = networkElements.map((ne: any, index: number) => {
      const img = hasVerified(ne.nrCount, ne.bdCount) ? PortGreen : PortGrey;
      const edit = (!ne.public) ? <img src={EditIcon} onClick={(e) => handleEditNEOpen(e, ne)} alt="edit" /> : <div />;
      const label = (
        <div className="split-label">
          <div>
            {(parseInt(ne.on_net) === 1) ? <img src={WaveXchangeIcon} alt="Company" /> : null}
            {ne.ne_name}
            <div style={{ paddingLeft: '5px' }}>{edit}</div>
          </div>
          <div>{(hasResources(ne.nrCount, ne.bdCount)) ? (<img src={img} alt="port" />) : null}</div>
        </div>
      );
      return (
        <div className="row" key={index + 1}>
          <FormControlLabel
            control={(
              <Checkbox
                classes={{
                  root: 'selection-checkbox',
                }}
                color="default"
                checked={!!selectedNEs.find((item: any) => item === ne.id)}
                onChange={() => toggleNE(ne.id)}
                icon={<img src={CheckboxIcon} alt="Checkbox" className="checkbox-icon" />}
                checkedIcon={<img src={CheckboxCheckedIcon} alt="Checkbox Checked" className="checkbox-checked-icon" />}
              />
            )}
            label={label}
          />
        </div>
      );
    });
    neRows = [addNodeButton, selectAllButton, neRows];
  }

  const handleClose = useCallback((selectedNEs?: any) => {
    onNeClose(selectedNEs);
    handleBack();
  }, [handleBack, onNeClose]);

  const handleSubmitClick = useCallback(() => {
    handleClose(selectedNEs);
  }, [handleClose, selectedNEs]);

  const handleAddNode = () => {
    const payload: submitNEType = {
      city, state, zip, name, country, street, longitude, latitude,
    };
    addNetworkElement(payload);
    handleBack();
  };
  const handleSubmitEdits = () => {
    if (!identifiers) return;
    const payload: editNEPayload = {
      city, state, zip, name, country, street, neId: identifiers.neId, nodeId: identifiers.nodeId, longitude, latitude,
    };
    editNetworkElement(payload);
    handleBack();
  };

  return (
    <DrawerMenu enableOverlay className={clsx('search-ne', open && 'open')} open={open} onClose={() => handleClose()} duration={100} direction="right">
      <div className="close-drawer-btn" onClick={() => (open ? handleClose() : onNeOpen())}>
        <img src={CloseBtn} alt="Close Drawer" />
      </div>
      {(nodePage !== 'closed') ? (
        <NodeForm
          showOnMap={showOnMap}
          setShowOnMap={setShowOnMap}
          originalNode={startingNode}
          type={nodePage}
          onClose={handleBack}
          addNode={handleAddNode}
          submitEdits={handleSubmitEdits}
          name={name}
          city={city}
          state={state}
          zip={zip}
          street={street}
          country={country}
          longitude={longitude}
          latitude={latitude}
          setLongitude={setLongitude}
          setLatitude={setLatitude}
          setName={setName}
          setCity={setCity}
          setState={setState}
          setZip={setZip}
          setStreet={setStreet}
          setCountry={setCountry}
        />
      ) : (
        <div className="select-ne">
          <label>Select Node</label>
          {/* <Dropdown
            options={dropdownList}
            value={dropdownValue}
            onChange={handleDropdownChange}
            customDisplayMethod={customDisplay}
          /> */}

          <div className="selection-box">
            <div>
              <Input
                classes={{
                  root: 'search-input-root',
                }}
                onChange={handleSearchChange}
                value={search}
                endAdornment={<InputAdornment position="end"><img src={SearchIconBtn} alt="Search" className="search-icon" /></InputAdornment>}
                placeholder="Search for a Node, City, or State (i.e. NY)"
              />
            </div>
            <div className="network-elemnts">
              {neRows}
            </div>
          </div>
          <Button
            disabled={areEqual}
            variant="primary"
            node="Update Nodes to Canvas"
            type="submit"
            onClick={() => handleSubmitClick()}
          />
        </div>
      )}
    </DrawerMenu>
  );
};

export default SelectNEDrawer;
